/**
 * Old Android WebViews render the app visibly off: below Chrome 88 Tailwind's button reset (`:where()`) is
 * dropped, so borderless buttons get the default gray box, and below 84 flex `gap` spacing is lost. Drivers on
 * such a WebView are blocked by a modal pointing at the Play Store package that updates it.
 * Sentry, Oct 2026: every driver was on 114 or later, so this only catches new drivers on very old phones.
 */
export const MIN_WEBVIEW_MAJOR = 88

export interface WebViewUpdate {
  name: string
  storeURL: string
}

/** Chrome major of an Android WebView user agent (`; wv)`), or null for anything else (iOS, browsers). */
export function androidWebViewMajor(userAgent: string): number | null {
  if (!/Android/.test(userAgent) || !/; wv\)/.test(userAgent)) return null
  const match = userAgent.match(/Chrome\/(\d+)/)
  return match ? Number(match[1]) : null
}

/**
 * What to update when the WebView is too old, or null when it's fine. On Android 7 to 9 Chrome itself provides
 * the WebView, so updating Chrome is what fixes it there.
 */
export function webviewUpdateFor(userAgent: string): WebViewUpdate | null {
  const major = androidWebViewMajor(userAgent)
  if (major == null || major >= MIN_WEBVIEW_MAJOR) return null
  if (/Android [789][.;)]/.test(userAgent)) {
    return { name: 'Google Chrome', storeURL: 'https://play.google.com/store/apps/details?id=com.android.chrome' }
  }
  return { name: 'Android System WebView', storeURL: 'https://play.google.com/store/apps/details?id=com.google.android.webview' }
}
